// SESSION SC7A SPLIT THIS PAGE INTO ITS TWO HALVES WITHOUT CHANGING EITHER. The calm menu puts the survey you SEND under Customers
// (Customer satisfaction, NpsSurveyPage) and the figures you READ under Reports (Customer satisfaction figures, NpsFigures). NpsPage is
// the two together, exactly as it always was, and stays on its old address (/office/nps) so every old link still opens the same page.
//
// Office NPS view (Session AE, Item 3): the post-service survey responses + a summary panel, the
// office-facing surface for feedback collected automatically after service jobs (Session T). NPS is
// (% promoters) - (% detractors), shown with a colour and a this-month-vs-last-month trend. Read-only:
// NpsResponse is a permanent customer-feedback record with no delete.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api, type NpsCategory, type NpsListItem, type NpsOfficeSummary } from "../../lib/api";
import { RecordPicker, type PickerRecord } from "../../components/RecordPicker";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { Button } from "../../components/Button";
import { PageHeader } from "../../components/PageHeader";
import { toneTextClass } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";

// The score badge colour by band: red 0-6, amber 7-8, green 9-10. The red fill is the fixed #c0392b
// token with white ink (5.44:1), never bg-ele-danger, which is #FF5A5A on the dark theme (3.06:1).
function scoreClass(score: number): string {
  if (score <= 6) return "bg-ele-danger-fill text-white";
  if (score <= 8) return "bg-amber-500 text-ele-bg";
  return "bg-ele-green text-ele-bg";
}

const TABS: { key: "all" | NpsCategory; label: string }[] = [
  { key: "all", label: "All" },
  { key: "promoter", label: "Promoters (9-10)" },
  { key: "passive", label: "Passives (7-8)" },
  { key: "detractor", label: "Detractors (0-6)" },
];

type SortKey = "recent" | "highest" | "lowest";

// The figures half: the summary and the response list, with the category tabs. `withSendAction` puts the Send NPS survey button back in the
// header, which is how the old /office/nps page (NpsPage below) reads; the Reports tab leaves it off, because sending lives under Customers.
export function NpsFigures({ withSendAction = false }: { withSendAction?: boolean }) {
  const [summary, setSummary] = useState<NpsOfficeSummary | null>(null);
  const [items, setItems] = useState<NpsListItem[]>([]);
  const [tab, setTab] = useState<"all" | NpsCategory>("all");
  const [sort, setSort] = useState<SortKey>("recent");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [sendOpen, setSendOpen] = useState(false);

  // The summary is overall (loaded once); the list reloads when the category tab changes.
  useEffect(() => {
    let live = true;
    api.nps
      .summary()
      .then((s) => live && setSummary(s))
      .catch(() => live && setError(true));
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.nps
      .list(tab === "all" ? undefined : tab)
      .then((rows) => {
        if (!live) return;
        setItems(rows);
        setLoading(false);
      })
      .catch(() => {
        if (!live) return;
        setError(true);
        setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [tab]);

  const sorted = useMemo(() => {
    const copy = [...items];
    if (sort === "highest") copy.sort((a, b) => b.score - a.score || Date.parse(b.respondedAt) - Date.parse(a.respondedAt));
    else if (sort === "lowest") copy.sort((a, b) => a.score - b.score || Date.parse(b.respondedAt) - Date.parse(a.respondedAt));
    // "recent" keeps the server's newest-first order.
    return copy;
  }, [items, sort]);

  const npsInk =
    summary?.npsScore == null ? "text-ele-strong" : toneTextClass(summary.npsScore > 0 ? "green" : summary.npsScore < 0 ? "red" : "amber");

  return (
    <div>
      {/* Session AHY: the survey is no longer sent automatically. It is drafted, and it waits
          in Gmail until someone reads it and presses send. */}
      <PageHeader
        title={withSendAction ? "Customer satisfaction (NPS)" : "Customer satisfaction figures"}
        subtitle="Net Promoter Score from the survey drafted after each service visit, for the office to check and send. NPS is the percentage of promoters minus the percentage of detractors."
        className="mb-6"
        actions={
          withSendAction ? (
            <Button onClick={() => setSendOpen(true)} className="flex-none">
              Send NPS survey
            </Button>
          ) : undefined
        }
      />

      {error && !summary && <p className="rounded-ele border border-ele-danger bg-ele-surface p-4 text-ele-danger">Could not load NPS data. Please try again.</p>}

      {/* Summary panel */}
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5">
        <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
          <div className="font-menu text-xs uppercase tracking-widest text-ele-blue">Overall NPS</div>
          <div className={`mt-2 font-heading text-3xl ${npsInk}`}>{summary?.npsScore ?? "-"}</div>
          <div className="mt-1 text-xs text-ele-body opacity-60">{trendLabel(summary)}</div>
        </div>
        <Metric label="Responses" value={summary ? String(summary.totalResponses) : "-"} />
        {/* "raised", not "sent" (Session AHY): the denominator counts every survey the platform has
            drafted, and whether the office pressed send on each one is a fact only Gmail holds. */}
        <Metric label="Response rate" value={summary?.responseRate == null ? "-" : `${summary.responseRate}%`} hint={summary ? `${summary.sent} raised` : undefined} />
        <Metric label="Average score" value={summary?.average == null ? "-" : `${summary.average} / 10`} />
        <Metric
          label="This month"
          value={summary?.trend.thisMonth == null ? "-" : String(summary.trend.thisMonth)}
          hint={summary?.trend.lastMonth == null ? "no last month" : `last month ${summary.trend.lastMonth}`}
        />
      </div>

      {/* Filter tabs + sort. Stacks below sm (640px), byte-identical row from sm up. */}
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="NPS category">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={`max-sm:min-h-11 rounded-ele px-3 py-1.5 text-sm transition ${tab === t.key ? "bg-ele-purple text-ele-white" : "border border-ele-purple-accent text-ele-body hover:text-ele-strong"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-ele-body">
          Sort by
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            className="max-sm:min-h-11 w-full rounded-ele border border-ele-purple-accent bg-ele-field-inset px-2 py-1 text-ele-strong focus:border-ele-green focus:outline-none sm:w-auto"
          >
            <option value="recent">Most recent</option>
            <option value="highest">Highest score</option>
            <option value="lowest">Lowest score</option>
          </select>
        </label>
      </div>

      {/* Response list */}
      {loading ? (
        <p className="text-ele-body opacity-70">Loading responses...</p>
      ) : sorted.length === 0 ? (
        <p className="rounded-ele border border-ele-purple-accent bg-ele-surface-soft p-6 text-center text-ele-body">No responses in this view yet.</p>
      ) : (
        <ul className="space-y-2">
          {sorted.map((r) => (
            <li key={r.id} className="rounded-ele border border-ele-purple-accent bg-ele-surface p-3">
              <div className="flex items-start gap-3">
                <span className={`flex h-9 w-9 flex-none items-center justify-center rounded-full font-heading text-sm ${scoreClass(r.score)}`} aria-label={`Score ${r.score}`}>
                  {r.score}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-heading text-ele-strong">{r.siteName}</span>
                    <span className="text-xs text-ele-body opacity-70">{formatDate(r.respondedAt)}</span>
                  </div>
                  <div className="text-sm text-ele-body">{r.customerName}</div>
                  {r.comment && (
                    <button
                      type="button"
                      onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                      className={`mt-1 block w-full text-left text-sm text-ele-body opacity-90 ${expanded === r.id ? "" : "truncate"}`}
                      title="Click to expand"
                    >
                      &ldquo;{r.comment}&rdquo;
                    </button>
                  )}
                  {r.jobId ? (
                    <Link to={`/office/jobsheets?job=${encodeURIComponent(r.jobId)}`} className="mt-1 inline-block text-xs text-ele-green hover:underline">
                      View service record
                    </Link>
                  ) : (
                    <span className="mt-1 inline-block text-xs text-ele-body opacity-60">Sent manually</span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {withSendAction && sendOpen && <SendNpsModal onClose={() => setSendOpen(false)} />}
    </div>
  );
}

/** The old /office/nps page: the figures and the Send NPS survey button together, unchanged. */
export function NpsPage() {
  return <NpsFigures withSendAction />;
}

/**
 * The survey half, under Customers: sending the standard satisfaction survey to a customer by hand. The surveys that follow a service visit
 * are drafted for the office to check and send from Gmail; this is the one place to send one yourself. The figures are under Reports.
 */
export function NpsSurveyPage() {
  const [sendOpen, setSendOpen] = useState(false);
  return (
    <div>
      <PageHeader
        title="Customer satisfaction"
        subtitle="Send the standard satisfaction survey to a customer by hand. The survey that follows each service visit is drafted for you to check and send from Gmail. The scores that come back are under Reports, in Customer satisfaction figures."
        className="mb-6"
        actions={
          <Button onClick={() => setSendOpen(true)} className="flex-none">
            Send NPS survey
          </Button>
        }
      />
      <p className="rounded-ele border border-ele-purple-accent bg-ele-surface-soft p-6 text-sm text-ele-body">
        Choose a customer, check the email address and send. The customer scores the survey from the link in the email, and their answer appears in Customer satisfaction figures.{" "}
        <Link to="/office/nps-figures" className="text-ele-green underline-offset-2 hover:underline">
          See the scores
        </Link>
      </p>
      {sendOpen && <SendNpsModal onClose={() => setSendOpen(false)} />}
    </div>
  );
}

// Manual NPS survey send (Session AR Item 18): pick a customer, confirm/override the email, add an
// optional internal note, and send the standard branded survey now. Reuses the existing send path.
function SendNpsModal({ onClose }: { onClose: () => void }) {
  const [customerId, setCustomerId] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  function pickCustomer(id: string, rec: PickerRecord | null) {
    setCustomerId(id);
    setEmail(typeof rec?.raw.billingEmail === "string" ? rec.raw.billingEmail : "");
  }

  async function send() {
    if (!customerId) {
      setError("Choose a customer to survey.");
      return;
    }
    setSending(true);
    setError(null);
    try {
      await api.nps.send({ customerId, email: email.trim() || undefined, note: note.trim() || undefined });
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not send the survey. Please try again.");
    } finally {
      setSending(false);
    }
  }

  return (
    <ModalShell ariaLabel="Send NPS survey" onClose={() => !sending && onClose()} closeOnBackdrop width="max-w-lg" panelClassName="p-6">
        <div className="mb-3 flex items-start justify-between gap-3">
          <h2 className="font-heading text-xl text-ele-blue">Send NPS survey</h2>
          <button onClick={onClose} aria-label="Close" className="text-ele-body hover:text-ele-green">✕</button>
        </div>

        {sent ? (
          <>
            <p role="status" className="rounded-ele border border-ele-green bg-ele-green/10 px-3 py-3 text-sm text-ele-green">
              Survey sent. The customer can score it from the link in the email; the response will appear here once they reply.
            </p>
            <div className="mt-4 flex justify-end">
              <Button onClick={onClose}>Done</Button>
            </div>
          </>
        ) : (
          <>
            <p className="mb-3 text-sm text-ele-body opacity-70">
              Sends the standard branded survey now, rather than waiting for the automatic post-service send.
            </p>
            <div className="mb-3">
              <RecordPicker kind="customer" id="nps-customer" label="Customer" value={customerId} placeholder="Search for a customer" onChange={(id, rec) => pickCustomer(id, rec)} />
            </div>

            <label className="mb-1 block text-sm" htmlFor="nps-email">Contact email</label>
            <input
              id="nps-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Prefilled from the customer; edit if needed"
              className="mb-3 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
            />

            <label className="mb-1 block text-sm" htmlFor="nps-note">Internal note (optional)</label>
            <textarea
              id="nps-note"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Recorded in the audit trail; not shown to the customer"
              className="mb-3 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
            />

            {error && <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{error}</p>}

            <div className={`gap-3 ${MODAL_ACTIONS}`}>
              <button onClick={onClose} disabled={sending} className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm disabled:opacity-50 ${MODAL_ACTION_BTN}`}>Cancel</button>
              <Button onClick={send} disabled={sending || !customerId} className={MODAL_ACTION_BTN}>
                {sending ? "Sending..." : "Send survey"}
              </Button>
            </div>
          </>
        )}
    </ModalShell>
  );
}

function Metric({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
      <div className="font-menu text-xs uppercase tracking-widest text-ele-blue">{label}</div>
      <div className="mt-2 font-heading text-3xl text-ele-strong">{value}</div>
      {hint ? <div className="mt-1 text-xs text-ele-body opacity-60">{hint}</div> : null}
    </div>
  );
}

function trendLabel(summary: NpsOfficeSummary | null): string {
  if (!summary || summary.trend.thisMonth == null || summary.trend.lastMonth == null) return "trend: not enough data";
  const arrow = summary.trend.direction === "up" ? "up" : summary.trend.direction === "down" ? "down" : "flat";
  return `trend: ${arrow} vs last month`;
}
